import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import {
  OnboardingAssistantButton,
  OnboardingChatPanel,
  OnboardingSpotlight,
} from '@/components/onboarding';
import { ToastContainer, showToast } from '@/components/ui/Toast';
import { UndoToastContainer } from '@/components/softdelete/UndoToast';
import { ReadinessNudgeContainer } from '@/components/readiness';
import { LifecycleBanner } from '@/components/subscription/LifecycleBanner';
import { useBillingInfo } from '@/hooks/useSubscription';
import { useSmartAssistant } from '@/hooks/useSmartAssistant';
import { useAssistantFormContext } from '@/contexts/AssistantFormContext';
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress';
import { useAssistantNudge } from '@/hooks/useAssistantNudge';
import { useNotificationSound } from '@/hooks/useChatFeatures';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useAuthReady } from '@/hooks/useAuthReady';
import { TIMING } from '@/lib/utils';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import type { AssistantAction } from '@/types/assistant';
import type { ActionFeedItem } from '@/hooks/useGuestActionsFeed';

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const location = useLocation();
  // Full-height pages with their own fixed composer at the bottom: no main
  // padding, and no floating assistant / nudge covering their buttons (the
  // pages offer the assistant in their own header).
  const isMessagesPage = location.pathname.includes('/messages') || location.pathname.startsWith('/dashboard/smart-inbox');
  // The setup wizard is a full-screen flow with its own inline helper ("Pitaj me")
  // and a bottom Next button — the floating assistant + nudges would cover it.
  const isWizardPage = location.pathname.startsWith('/dashboard/quick-start');
  const { t } = useTranslation();
  // Chat presence: a heartbeat every minute while any dashboard page is
  // visible (guests see the host "online" only with a fresh heartbeat).
  const { userId: presenceUserId } = useAuthReady();
  useOnlineStatus(presenceUserId || undefined);

  const navigate = useNavigate();
  const formContext = useAssistantFormContext();
  const { playSound } = useNotificationSound();
  const { isNewUser, isOnboardingComplete, isLoading: onboardingLoading } = useOnboardingProgress();
  const nudge = useAssistantNudge();

  const {
    messages,
    isLoading: assistantLoading,
    sendMessage,
    clearMessages,
    executeAction,
    quickSuggestions,
    welcomeMessage,
    runSetupCheck,
    guidedMode,
    dismissGuide,
    mode,
    switchToSupport,
    switchToAssistant,
  } = useSmartAssistant({ formContext: formContext || undefined });

  // Listen for events requesting the assistant to open (e.g. from AddPropertyChoiceModal)
  useEffect(() => {
    const handleOpenAssistant = () => {
      // Reset to assistant mode if previously in support
      if (mode === 'support') {
        switchToAssistant();
      }
      setShowAssistant(true);
      if (messages.length === 0) {
        runSetupCheck();
      }
    };
    const handleOpenSupport = () => {
      switchToSupport();
      setShowAssistant(true);
    };
    window.addEventListener('open-smart-assistant', handleOpenAssistant);
    window.addEventListener('open-support-mode', handleOpenSupport);
    return () => {
      window.removeEventListener('open-smart-assistant', handleOpenAssistant);
      window.removeEventListener('open-support-mode', handleOpenSupport);
    };
  }, [messages.length, runSetupCheck, switchToSupport, switchToAssistant, mode]);

  // NOTE: The assistant deliberately does NOT auto-open for new users.
  // Onboarding (Welcome modal → setup wizard) is the primary first-run flow.
  // The assistant stays a quiet floating button and only opens when the
  // landlord clicks it (or via the open-smart-assistant event above).

  // Global listener: play sound + show toast when new guest action arrives
  useEffect(() => {
    const actionLabels: Record<string, { icon: string; label: string }> = {
      departed: { icon: '🚗', label: t('actionsFeed.departed', 'Krenuo/la') },
      at_parking: { icon: '🅿️', label: t('actionsFeed.at_parking', 'Na parkingu') },
      arrived: { icon: '🏠', label: t('actionsFeed.arrived', 'Stigao/la') },
      location_shared: { icon: '📍', label: t('actionsFeed.location_shared', 'Lokacija') },
    };

    const handleNewAction = (event: Event) => {
      const action = (event as CustomEvent<ActionFeedItem>).detail;
      const info = actionLabels[action.action_type] || { icon: '🔔', label: action.action_type };

      playSound();
      showToast({
        icon: <span className="text-base">{info.icon}</span>,
        title: `${info.icon} ${action.guest_name}`,
        message: `${info.label} · ${action.unit_name}`,
        actionUrl: `/dashboard/bookings/${action.booking_id}`,
        duration: TIMING.TOAST_DURATION,
      });
    };

    window.addEventListener('guest-action-new', handleNewAction);
    return () => window.removeEventListener('guest-action-new', handleNewAction);
  }, [playSound, t]);

  const handleToggleAssistant = () => {
    const nextOpen = !showAssistant;
    setShowAssistant(nextOpen);
    if (nextOpen) {
      // Always reset to assistant mode when opening via the FAB button
      // (support mode should only be active when explicitly triggered)
      if (mode === 'support') {
        switchToAssistant();
      }
      if (messages.length === 0) {
        runSetupCheck();
      }
    }
  };

  // Close panel after navigation actions so user can see the destination page
  const handleExecuteAction = useCallback((action: AssistantAction) => {
    executeAction(action);
    if (action.type === 'navigate') {
      setShowAssistant(false);
    }
  }, [executeAction]);

  // Plan state banner (plan ended / suspended / card failed / renewal off /
  // gift ending) above every inner page. The dashboard home and Billing show
  // their own (Billing also explains a blocked account in full), and the
  // full-height chat + wizard pages have no room for it. Hosts WITHOUT access
  // are kept on Billing / Account / Support by RequireSubscription — there is
  // no full-screen block any more, so they can actually pay.
  const { data: billing } = useBillingInfo();
  const isOwnBannerPage =
    location.pathname === '/dashboard' ||
    location.pathname === '/dashboard/' ||
    location.pathname.startsWith('/dashboard/billing');
  const showLifecycleBanner = !isOwnBannerPage && !isMessagesPage && !isWizardPage;
  const isAccountPage = location.pathname.startsWith('/dashboard/account');

  return (
    <div className="flex min-h-screen">
      {/* Skip-to-content link - visible on keyboard focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-medium focus:shadow-lg focus:outline-none"
      >
        {t('nav.skipToContent', 'Preskoči na sadržaj')}
      </a>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-1 flex-col min-w-0">
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main id="main-content" tabIndex={-1} className={`flex-1 overflow-y-auto overflow-x-hidden ${isMessagesPage ? 'p-0' : 'p-4 md:p-6'}`}>
          {showLifecycleBanner && (
            <div className="mb-3 md:mb-5 empty:hidden">
              <LifecycleBanner
                billingInfo={billing}
                hide={isAccountPage ? ['deletion_pending'] : []}
              />
            </div>
          )}
          <ErrorBoundary level="page">
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Contextual Mini-Card for empty pages */}
      {nudge.miniCard && !showAssistant && !isMessagesPage && !isWizardPage && (
        <div className="fixed z-40 bottom-24 right-4 md:right-6 animate-in fade-in slide-in-from-bottom-3 duration-500">
          <button
            onClick={() => {
              if (nudge.miniCard!.action.startsWith('/')) {
                navigate(nudge.miniCard!.action);
              } else if (nudge.miniCard!.action === 'add-unit') {
                // Open assistant for guidance
                setShowAssistant(true);
              } else if (nudge.miniCard!.action === 'new-update') {
                // Dispatch event for host updates page
                window.dispatchEvent(new CustomEvent('create-host-update'));
              }
            }}
            className="flex items-center gap-2.5 px-4 py-3 bg-white dark:bg-slate-800 rounded-xl shadow-lg shadow-black/10 dark:shadow-black/30 border border-slate-200 dark:border-slate-700 hover:shadow-xl hover:scale-[1.02] transition-all duration-200 group"
          >
            <span className="text-xl">{nudge.miniCard.icon}</span>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {nudge.miniCard.text}
            </span>
            <span className="text-slate-400 group-hover:text-blue-500 transition-colors">→</span>
          </button>
        </div>
      )}

      {/* Floating AI Assistant Button - hidden on mobile when on messages page to avoid overlapping send button */}
      <OnboardingAssistantButton
        isOpen={showAssistant}
        onClick={handleToggleAssistant}
        isNewUser={!onboardingLoading && isNewUser}
        showBadge={!onboardingLoading && !isOnboardingComplete}
        className={isMessagesPage || isWizardPage ? 'hidden' : undefined}
        hasAlert={nudge.hasAlert}
        alertType={nudge.alertType}
        nudgeText={nudge.nudgeText}
      />

      {/* AI Assistant Chat Panel */}
      <OnboardingChatPanel
        isOpen={showAssistant}
        onClose={() => setShowAssistant(false)}
        messages={messages}
        isLoading={assistantLoading}
        onSendMessage={sendMessage}
        onExecuteAction={handleExecuteAction}
        quickSuggestions={quickSuggestions}
        welcomeMessage={welcomeMessage}
        onClear={clearMessages}
        guidedMode={guidedMode && isNewUser}
        onDismissGuide={dismissGuide}
        mode={mode}
        onSwitchMode={() => mode === 'support' ? switchToAssistant() : switchToSupport()}
      />

      {/* Onboarding spotlight (highlights specific UI elements during guided setup) */}
      <OnboardingSpotlight />

      {/* Global Toast Notifications */}
      <ToastContainer />

      {/* Soft Delete Undo Toast */}
      <UndoToastContainer />

      {/* Proactive readiness nudge after first save */}
      <ReadinessNudgeContainer />
    </div>
  );
}

export default DashboardLayout;
